import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken } from "../lib/api";
import { ArrowLeft, Save, Download, Link as LinkIcon, X, Plus, GripVertical, Eye, EyeOff, Trash2, Sparkles, Copy, Clock, BookmarkPlus, Bookmark, FileDown } from "lucide-react";

const DEFAULT_TEMPLATE = [
  { label: "Early Morning", time: "7:00 am" },
  { label: "Breakfast", time: "9:00 am" },
  { label: "Mid-Morning", time: "12:00 pm" },
  { label: "Lunch", time: "2:00 pm" },
  { label: "Evening", time: "5:00 pm" },
  { label: "Dinner", time: "8:00 pm" },
  { label: "Post-Dinner", time: "10:30 pm" },
];

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoDateList(start, end) {
  if (!start || !end) return [];
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (isNaN(+s) || isNaN(+e) || e < s) return [];
  const out = [];
  const d = new Date(s);
  while (d <= e) {
    out.push(toISODateLocal(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
function formatDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return { weekday: WEEKDAY_FULL[d.getDay()], short: d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) };
}
function newId() {
  return "e_" + Math.random().toString(36).slice(2, 10);
}
function defaultEntries() {
  return DEFAULT_TEMPLATE.map((t) => ({ id: newId(), label: t.label, time: t.time, text: "", remarks: "", recipe_ids: [] }));
}

function RecipePicker({ open, onPick, onClose, exclude = [] }) {
  const [q, setQ] = useState("");
  const [recipes, setRecipes] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get(`/admin/recipes?search=${encodeURIComponent(q)}&limit=50`).then(({ data }) => setRecipes(data));
  }, [q, open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl border border-[hsl(var(--brand-border))] shadow-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[hsl(var(--brand-border))]">
          <div className="font-display text-2xl">Attach a recipe</div>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="mt-3 w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="recipe-picker-search" />
        </div>
        <div className="overflow-y-auto p-3">
          {recipes.length === 0 ? <div className="p-4 text-sm font-serif-italic text-[hsl(var(--brand-muted))]">No matches</div> : null}
          {recipes.map((r) => (
            <button
              key={r.id}
              onClick={() => onPick(r)}
              disabled={exclude.includes(r.id)}
              className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--brand-cream))] rounded-md flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`recipe-pick-${r.id}`}
            >
              {r.image_url ? <img src={r.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${r.image_url}` : r.image_url} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-[hsl(var(--brand-cream-deep))]" />}
              <div className="flex-1">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-[hsl(var(--brand-muted))]">{r.tags?.slice(0, 3).join(" · ")}</div>
              </div>
              {exclude.includes(r.id) ? <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--brand-muted))]">Added</span> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MealAutocomplete({ value, onChange, onPickRecipe, testid, excludeRecipeIds = [] }) {
  const [focused, setFocused] = useState(false);
  const [sug, setSug] = useState([]);
  const [justPicked, setJustPicked] = useState(false);

  useEffect(() => {
    if (justPicked) { setJustPicked(false); return; }
    const q = (value || "").trim();
    if (!focused || q.length < 2) { setSug([]); return; }
    const t = setTimeout(() => {
      api.get(`/admin/recipes?search=${encodeURIComponent(q)}&limit=6`).then(({ data }) => {
        setSug((data || []).filter((r) => !excludeRecipeIds.includes(r.id)));
      });
    }, 180);
    return () => clearTimeout(t);
  }, [value, focused, justPicked]); // eslint-disable-line

  return (
    <div className="relative">
      <textarea
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Meal (start typing to see recipes)"
        rows={2}
        className="w-full px-3 py-2 text-sm border border-[hsl(var(--brand-border))] rounded-md resize-y"
        data-testid={testid}
      />
      {focused && sug.length > 0 ? (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-[hsl(var(--brand-border))] rounded-md shadow-lg max-h-64 overflow-y-auto">
          {sug.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setJustPicked(true); setSug([]); onPickRecipe(r); onChange(""); }}
              className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--brand-cream))] flex items-center gap-2"
              data-testid={`${testid}-sug-${r.id}`}
            >
              {r.image_url ? <img src={r.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${r.image_url}` : r.image_url} alt="" className="w-7 h-7 rounded object-cover" /> : <div className="w-7 h-7 rounded bg-[hsl(var(--brand-cream-deep))]" />}
              <div className="text-sm flex-1">
                {r.name}
                <div className="text-[10px] text-[hsl(var(--brand-muted))]">Tap to attach recipe</div>
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TemplateModal({ modal, dayTpls, weekTpls, planDayCount, onClose, onSaveDay, onApplyDay, onSaveWeek, onApplyWeek, onDelete }) {
  const [name, setName] = useState("");
  const isSave = modal.mode === "save-day" || modal.mode === "save-week";
  const isDay = modal.mode === "save-day" || modal.mode === "apply-day";
  const list = isDay ? dayTpls : weekTpls;

  const titleMap = {
    "save-day": "Save day as template",
    "apply-day": "Replace this day with a template",
    "save-week": "Save week as template",
    "apply-week": "Load week template into this plan",
  };
  const subMap = {
    "save-day": "This day's meals will be saved so you can drop them onto any future day.",
    "apply-day": "Replaces every meal currently on this day. Existing recipes attached to this day will be replaced.",
    "save-week": "All days in the current plan will be saved as one bundle.",
    "apply-week": `Replaces every day in the current plan. Template must have the same number of days as your plan (${planDayCount}).`,
  };

  const scopeBadge = isDay
    ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[hsl(var(--brand-sage))]/15 text-[hsl(var(--brand-sage))] border border-[hsl(var(--brand-sage))]/30">Day</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[hsl(var(--brand-terracotta))]/15 text-[hsl(var(--brand-terracotta))] border border-[hsl(var(--brand-terracotta))]/30">Full week</span>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 border border-[hsl(var(--brand-border))] shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="template-modal">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-2xl">{titleMap[modal.mode]}</div>
          {scopeBadge}
        </div>
        <p className="text-sm text-[hsl(var(--brand-muted))] mt-1">{subMap[modal.mode]}</p>

        {isSave ? (
          <>
            <label className="block mt-5 text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Template name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isDay ? "e.g., Vegan workday" : "e.g., 7-day weight loss starter"}
              className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md"
              data-testid="template-name-input"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))] text-sm">Cancel</button>
              <button
                onClick={() => (modal.mode === "save-day" ? onSaveDay(name) : onSaveWeek(name))}
                disabled={!name.trim()}
                className="px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-50 text-sm"
                data-testid="template-save-confirm"
              >
                Save template
              </button>
            </div>
          </>
        ) : (
          <>
            {list.length === 0 ? (
              <div className="mt-5 text-sm text-[hsl(var(--brand-muted))] font-serif-italic">No saved {isDay ? "day" : "week"} templates yet.</div>
            ) : (
              <div className="mt-5 max-h-72 overflow-y-auto space-y-2">
                {list.map((t) => {
                  const incompatible = !isDay && t.day_count !== planDayCount;
                  return (
                    <div
                      key={t.id}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md border border-[hsl(var(--brand-border))] ${incompatible ? "opacity-50" : "hover:bg-[hsl(var(--brand-cream))]"}`}
                      data-testid={`template-item-${t.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => !incompatible && (isDay ? onApplyDay(t.id) : onApplyWeek(t.id))}
                        disabled={incompatible}
                        className="flex-1 text-left disabled:cursor-not-allowed"
                      >
                        <div className="text-sm font-medium">{t.name}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">
                          {isDay
                            ? `${t.entry_count || 0} meal${t.entry_count === 1 ? "" : "s"}`
                            : `${t.day_count} day${t.day_count === 1 ? "" : "s"} · ${t.entry_count || 0} meal${t.entry_count === 1 ? "" : "s"}`}
                          {incompatible ? <span className="ml-2 text-[hsl(var(--destructive))]">— size mismatch</span> : null}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(t.id)}
                        className="p-1.5 rounded-md hover:bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"
                        title="Delete template"
                        data-testid={`template-delete-${t.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))] text-sm">Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminPlanBuilder() {
  const { clientId: routeClientId, planId } = useParams();
  const nav = useNavigate();
  const editing = Boolean(planId);

  const [client, setClient] = useState(null);
  const [plan, setPlan] = useState(() => {
    const today = new Date();
    const weekLater = new Date();
    weekLater.setDate(today.getDate() + 6);
    const pad = (n) => String(n).padStart(2, "0");
    const local = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return {
      title: "Weekly Diet Plan",
      start_date: local(today),
      end_date: local(weekLater),
      notes: "",
      visible_to_client: true,
      days: {},
    };
  });
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // { iso, entryId }
  const [recipeMap, setRecipeMap] = useState({}); // id -> {name, image_url}
  const [dupModal, setDupModal] = useState(null); // { sourceIso, selected: Set<iso> }
  const [tplModal, setTplModal] = useState(null); // { mode: "save-day"|"apply-day"|"save-week"|"apply-week", iso? }
  const [dayTpls, setDayTpls] = useState([]);    // [{ id, name, entry_count, created_at }]
  const [weekTpls, setWeekTpls] = useState([]);  // [{ id, name, day_count, ... }]

  // Ensure every date in [start..end] has an entries list. Preserve existing data.
  const syncDaysWithRange = (planState) => {
    const dates = isoDateList(planState.start_date, planState.end_date);
    const next = {};
    for (const iso of dates) {
      next[iso] = planState.days[iso] || { entries: defaultEntries() };
    }
    return next;
  };

  useEffect(() => {
    (async () => {
      if (editing) {
        const { data } = await api.get(`/admin/plans/${planId}`);
        setClient(data.client);
        const incoming = data.plan;
        const rawDays = incoming.days || {};
        const normalized = {};
        for (const [iso, day] of Object.entries(rawDays)) {
          normalized[iso] = {
            entries: (day.entries || []).map((e) => ({
              id: e.id || newId(),
              label: e.label || "",
              time: e.time || "",
              text: e.text || "",
              remarks: e.remarks || "",
              recipe_ids: e.recipe_ids || (e.recipe_id ? [e.recipe_id] : []),
            })),
          };
        }
        const merged = {
          title: incoming.title,
          start_date: incoming.start_date,
          end_date: incoming.end_date,
          notes: incoming.notes || "",
          visible_to_client: incoming.visible_to_client !== false,
          days: normalized,
        };
        merged.days = syncDaysWithRange(merged);
        setPlan(merged);
        // Load recipe names/images
        const ids = new Set();
        Object.values(merged.days).forEach((d) => d.entries.forEach((e) => (e.recipe_ids || []).forEach((rid) => ids.add(rid))));
        if (ids.size) {
          const { data: all } = await api.get("/admin/recipes?limit=1000");
          const nm = {};
          all.forEach((r) => { if (ids.has(r.id)) nm[r.id] = { name: r.name, image_url: r.image_url }; });
          setRecipeMap(nm);
        }
      } else if (routeClientId) {
        const { data } = await api.get(`/admin/clients/${routeClientId}`);
        setClient(data.client);
        setPlan((p) => ({ ...p, days: syncDaysWithRange(p) }));
      }
    })();
    // eslint-disable-next-line
  }, [editing, planId, routeClientId]);

  // When start/end dates change, sync day list
  useEffect(() => {
    setPlan((p) => ({ ...p, days: syncDaysWithRange(p) }));
    // eslint-disable-next-line
  }, [plan.start_date, plan.end_date]);

  const updateEntry = (iso, entryId, field, value) => {
    setPlan((p) => {
      const day = p.days[iso] || { entries: [] };
      const entries = day.entries.map((e) => (e.id === entryId ? { ...e, [field]: value } : e));
      return { ...p, days: { ...p.days, [iso]: { entries } } };
    });
  };
  const addEntry = (iso, preset = null) => {
    const e = preset || { id: newId(), label: "New slot", time: "", text: "", remarks: "", recipe_ids: [] };
    setPlan((p) => {
      const day = p.days[iso] || { entries: [] };
      return { ...p, days: { ...p.days, [iso]: { entries: [...day.entries, { ...e, id: newId() }] } } };
    });
  };
  const removeEntry = (iso, entryId) => {
    setPlan((p) => {
      const day = p.days[iso] || { entries: [] };
      return { ...p, days: { ...p.days, [iso]: { entries: day.entries.filter((e) => e.id !== entryId) } } };
    });
  };
  const moveEntry = (iso, entryId, dir) => {
    setPlan((p) => {
      const day = p.days[iso] || { entries: [] };
      const entries = [...day.entries];
      const i = entries.findIndex((e) => e.id === entryId);
      if (i < 0) return p;
      const j = i + dir;
      if (j < 0 || j >= entries.length) return p;
      [entries[i], entries[j]] = [entries[j], entries[i]];
      return { ...p, days: { ...p.days, [iso]: { entries } } };
    });
  };
  const attachRecipe = (iso, entryId, recipe) => {
    setPlan((p) => {
      const day = p.days[iso] || { entries: [] };
      const entries = day.entries.map((e) => {
        if (e.id !== entryId) return e;
        const recipe_ids = Array.from(new Set([...(e.recipe_ids || []), recipe.id]));
        return { ...e, recipe_ids };
      });
      return { ...p, days: { ...p.days, [iso]: { entries } } };
    });
    setRecipeMap((nm) => ({ ...nm, [recipe.id]: { name: recipe.name, image_url: recipe.image_url } }));
  };
  const detachRecipe = (iso, entryId, recipeId) => {
    setPlan((p) => {
      const day = p.days[iso] || { entries: [] };
      const entries = day.entries.map((e) => (e.id === entryId ? { ...e, recipe_ids: (e.recipe_ids || []).filter((r) => r !== recipeId) } : e));
      return { ...p, days: { ...p.days, [iso]: { entries } } };
    });
  };

  // Copy ALL meals from sourceIso → each iso in targetIsos. Replaces target days' entries.
  const duplicateDayToOthers = (sourceIso, targetIsos) => {
    const sourceEntries = plan.days[sourceIso]?.entries || [];
    if (sourceEntries.length === 0) {
      toast.error("This day has no meals to copy");
      return;
    }
    if (targetIsos.length === 0) {
      toast.error("Pick at least one day");
      return;
    }
    const newDays = { ...plan.days };
    for (const targetIso of targetIsos) {
      newDays[targetIso] = {
        entries: sourceEntries.map((e) => ({
          ...e,
          id: newId(),
          recipe_ids: [...(e.recipe_ids || [])],
        })),
      };
    }
    setPlan((p) => ({ ...p, days: newDays }));
    toast.success(`Day copied to ${targetIsos.length} other day${targetIsos.length === 1 ? "" : "s"}`);
  };

  // Apply ONE slot's time to the same-labelled slot on every other day. Leaves meals untouched.
  const applySlotTimeToAllDays = (iso, entryId) => {
    const sourceEntry = plan.days[iso]?.entries?.find((e) => e.id === entryId);
    if (!sourceEntry) return;
    const label = (sourceEntry.label || "").trim();
    const time = (sourceEntry.time || "").trim();
    if (!label || !time) {
      toast.error("Set both a slot label and a time first");
      return;
    }
    let updated = 0;
    const newDays = { ...plan.days };
    for (const [otherIso, day] of Object.entries(newDays)) {
      if (otherIso === iso) continue;
      let dayChanged = false;
      const newEntries = (day.entries || []).map((e) => {
        if ((e.label || "").trim().toLowerCase() === label.toLowerCase() && e.time !== time) {
          dayChanged = true;
          updated++;
          return { ...e, time };
        }
        return e;
      });
      if (dayChanged) newDays[otherIso] = { entries: newEntries };
    }
    if (updated === 0) {
      toast.success(`No other "${label}" slots needed updating`);
      return;
    }
    setPlan((p) => ({ ...p, days: newDays }));
    toast.success(`Time applied to ${updated} ${label} slot${updated === 1 ? "" : "s"} across the week`);
  };

  // ---------- Templates: fetch list, save, apply, delete ----------
  const refreshTemplates = async () => {
    try {
      const [{ data: dayList }, { data: weekList }] = await Promise.all([
        api.get("/admin/templates?scope=day"),
        api.get("/admin/templates?scope=week"),
      ]);
      setDayTpls(dayList || []);
      setWeekTpls(weekList || []);
    } catch (err) {
      // Non-fatal — template list is just a sidekick to the main plan editor
    }
  };

  useEffect(() => { refreshTemplates(); }, []);

  const saveDayAsTemplate = async (iso, name) => {
    const entries = plan.days[iso]?.entries || [];
    if (entries.length === 0) { toast.error("This day has no meals to save"); return; }
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Give the template a name"); return; }
    try {
      await api.post("/admin/templates/day", { name: trimmed, entries });
      toast.success(`Day template "${trimmed}" saved`);
      await refreshTemplates();
      setTplModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const applyDayTemplate = async (iso, templateId) => {
    try {
      const { data } = await api.get(`/admin/templates/${templateId}`);
      const newEntries = (data.entries || []).map((e) => ({
        id: newId(),
        label: e.label || "",
        time: e.time || "",
        text: e.text || "",
        remarks: e.remarks || "",
        recipe_ids: [...(e.recipe_ids || [])],
      }));
      setPlan((p) => ({ ...p, days: { ...p.days, [iso]: { entries: newEntries } } }));
      toast.success(`Applied "${data.name}" to this day`);
      // Refresh recipe-name cache for any new attached recipes
      const ids = new Set();
      newEntries.forEach((e) => (e.recipe_ids || []).forEach((rid) => ids.add(rid)));
      const missing = [...ids].filter((rid) => !recipeMap[rid]);
      if (missing.length) {
        const { data: all } = await api.get("/admin/recipes?limit=1000");
        const nm = { ...recipeMap };
        all.forEach((r) => { if (ids.has(r.id)) nm[r.id] = { name: r.name, image_url: r.image_url }; });
        setRecipeMap(nm);
      }
      setTplModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply template");
    }
  };

  const saveWeekAsTemplate = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Give the template a name"); return; }
    const dates = isoDateList(plan.start_date, plan.end_date);
    const days = dates.map((iso) => plan.days[iso]?.entries || []);
    if (days.every((d) => d.length === 0)) {
      toast.error("Plan is empty — add meals first");
      return;
    }
    try {
      await api.post("/admin/templates/week", { name: trimmed, days });
      toast.success(`Week template "${trimmed}" saved (${dates.length} day${dates.length === 1 ? "" : "s"})`);
      await refreshTemplates();
      setTplModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const applyWeekTemplate = async (templateId) => {
    try {
      const { data } = await api.get(`/admin/templates/${templateId}`);
      const dates = isoDateList(plan.start_date, plan.end_date);
      const tplDays = data.days || [];
      if (tplDays.length !== dates.length) {
        toast.error(`Template has ${tplDays.length} days but plan has ${dates.length}. Adjust the date range first.`);
        return;
      }
      const newDays = {};
      dates.forEach((iso, idx) => {
        const tplEntries = tplDays[idx] || [];
        newDays[iso] = {
          entries: tplEntries.map((e) => ({
            id: newId(),
            label: e.label || "",
            time: e.time || "",
            text: e.text || "",
            remarks: e.remarks || "",
            recipe_ids: [...(e.recipe_ids || [])],
          })),
        };
      });
      setPlan((p) => ({ ...p, days: newDays }));
      toast.success(`Applied "${data.name}" — ${dates.length} day${dates.length === 1 ? "" : "s"} populated`);
      // Refresh recipe-name cache
      const ids = new Set();
      Object.values(newDays).forEach((d) => d.entries.forEach((e) => (e.recipe_ids || []).forEach((rid) => ids.add(rid))));
      const missing = [...ids].filter((rid) => !recipeMap[rid]);
      if (missing.length) {
        const { data: all } = await api.get("/admin/recipes?limit=1000");
        const nm = { ...recipeMap };
        all.forEach((r) => { if (ids.has(r.id)) nm[r.id] = { name: r.name, image_url: r.image_url }; });
        setRecipeMap(nm);
      }
      setTplModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply template");
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template? This can't be undone.")) return;
    try {
      await api.delete(`/admin/templates/${templateId}`);
      await refreshTemplates();
      toast.success("Template deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const dateList = useMemo(() => isoDateList(plan.start_date, plan.end_date), [plan.start_date, plan.end_date]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/plans/${planId}`, plan);
        toast.success("Plan saved");
      } else {
        const { data } = await api.post(`/admin/clients/${routeClientId}/plans`, plan);
        toast.success("Plan created");
        nav(`/admin/plans/${data.id}/edit`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = () => {
    if (!editing) { toast.error("Save the plan first"); return; }
    const t = getToken();
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/plans/${planId}/pdf?auth=${t}`, "_blank");
  };

  if (!client) return <div className="font-serif-italic text-[hsl(var(--brand-muted))]">Loading plan builder…</div>;

  return (
    <div data-testid="plan-builder-page">
      <Link to={`/admin/clients/${client.id}`} className="text-sm text-[hsl(var(--brand-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--brand-ink))]">
        <ArrowLeft className="w-3 h-3" /> Back to {client.name}
      </Link>
      <div className="mt-4 flex items-start justify-between flex-wrap gap-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">
            {editing ? "Edit plan" : "New plan"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-1">For {client.name}</h1>
          <p className="text-[hsl(var(--brand-muted))] mt-2 text-sm">
            {dateList.length > 0 ? `${dateList.length} day${dateList.length === 1 ? "" : "s"}` : "Set a start and end date"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {weekTpls.length > 0 ? (
            <button
              onClick={() => setTplModal({ mode: "apply-week" })}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))] text-sm"
              data-testid="apply-week-template-btn"
              title="Load a saved full-week template into this plan"
            >
              <FileDown className="w-4 h-4" /> Load week template
            </button>
          ) : null}
          <button
            onClick={() => setTplModal({ mode: "save-week" })}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))] text-sm"
            data-testid="save-week-template-btn"
            title="Save this whole plan as a reusable full-week template"
          >
            <BookmarkPlus className="w-4 h-4" /> Save full week
          </button>
          <button
            onClick={() => setPlan((p) => ({ ...p, visible_to_client: !p.visible_to_client }))}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border text-sm ${plan.visible_to_client ? "bg-[hsl(var(--brand-sage))]/10 border-[hsl(var(--brand-sage))]/30 text-[hsl(var(--brand-sage))]" : "bg-white border-[hsl(var(--brand-border))] text-[hsl(var(--brand-muted))]"}`}
            data-testid="toggle-visibility-btn"
          >
            {plan.visible_to_client ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {plan.visible_to_client ? "Visible to client" : "Hidden from client"}
          </button>
          {editing ? (
            <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[hsl(var(--brand-border))] hover:bg-[hsl(var(--brand-cream))] text-sm" data-testid="download-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </button>
          ) : null}
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-60" data-testid="save-plan-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save plan"}
          </button>
        </div>
      </div>

      {/* Plan meta */}
      <div className="mt-8 grid md:grid-cols-4 gap-4 bg-white border border-[hsl(var(--brand-border))] rounded-xl p-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Title</label>
          <input value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="plan-title-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Start date</label>
          <input type="date" value={plan.start_date} onChange={(e) => setPlan({ ...plan, start_date: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="plan-start-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">End date</label>
          <input type="date" value={plan.end_date} onChange={(e) => setPlan({ ...plan, end_date: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="plan-end-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1.5">Notes</label>
          <input value={plan.notes} onChange={(e) => setPlan({ ...plan, notes: e.target.value })} placeholder="General note" className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="plan-notes-input" />
        </div>
      </div>

      {/* Day cards */}
      <div className="mt-8 space-y-6">
        {dateList.length === 0 ? (
          <div className="p-10 text-center bg-white border border-[hsl(var(--brand-border))] rounded-xl font-serif-italic text-[hsl(var(--brand-muted))]">
            Pick a start and end date to begin.
          </div>
        ) : null}
        {dateList.map((iso) => {
          const { weekday, short } = formatDate(iso);
          const entries = plan.days[iso]?.entries || [];
          return (
            <div key={iso} className="bg-white border border-[hsl(var(--brand-border))] rounded-xl overflow-hidden" data-testid={`day-card-${iso}`}>
              <div className="px-5 py-3 bg-[hsl(var(--brand-sage))] text-white flex items-center justify-between gap-3">
                <div>
                  <div className="font-display text-2xl">{weekday}</div>
                  <div className="text-xs tracking-[0.2em] uppercase opacity-80">{short}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <div className="text-xs tracking-[0.2em] uppercase opacity-80">{entries.length} meal{entries.length === 1 ? "" : "s"}</div>
                  {entries.length > 0 ? (
                    <button
                      onClick={() => setTplModal({ mode: "save-day", iso })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-xs"
                      data-testid={`save-day-template-${iso}`}
                      title="Save this single day as a reusable day-template"
                    >
                      <BookmarkPlus className="w-3 h-3" /> Save day as template
                    </button>
                  ) : null}
                  {dayTpls.length > 0 ? (
                    <button
                      onClick={() => setTplModal({ mode: "apply-day", iso })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-xs"
                      data-testid={`apply-day-template-${iso}`}
                      title="Replace this day with a saved day-template"
                    >
                      <Bookmark className="w-3 h-3" /> Use day template
                    </button>
                  ) : null}
                  {dateList.length > 1 && entries.length > 0 ? (
                    <button
                      onClick={() => setDupModal({ sourceIso: iso, selected: new Set() })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-xs"
                      data-testid={`duplicate-day-${iso}`}
                      title="Duplicate this day's meals to other days"
                    >
                      <Copy className="w-3 h-3" /> Duplicate to…
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="divide-y divide-[hsl(var(--brand-border))]">
                {entries.map((entry, idx) => (
                  <div key={entry.id} className="p-4" data-testid={`entry-${iso}-${entry.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr_auto] gap-3 items-start">
                      <div>
                        <div className="flex items-center gap-1 text-[hsl(var(--brand-muted))] text-[10px] tracking-[0.2em] uppercase mb-1">
                          <GripVertical className="w-3 h-3" />
                          <button onClick={() => moveEntry(iso, entry.id, -1)} disabled={idx === 0} className="disabled:opacity-30" data-testid={`move-up-${iso}-${entry.id}`} title="Move up">▲</button>
                          <button onClick={() => moveEntry(iso, entry.id, 1)} disabled={idx === entries.length - 1} className="disabled:opacity-30" data-testid={`move-down-${iso}-${entry.id}`} title="Move down">▼</button>
                        </div>
                        <input
                          value={entry.label}
                          onChange={(e) => updateEntry(iso, entry.id, "label", e.target.value)}
                          placeholder="Slot name"
                          className="w-full px-2 py-1 text-xs font-medium uppercase tracking-[0.15em] border border-[hsl(var(--brand-border))] rounded bg-[hsl(var(--brand-cream-deep))]"
                          data-testid={`label-${iso}-${entry.id}`}
                        />
                        <div className="mt-2 flex items-center gap-1">
                          <input
                            value={entry.time}
                            onChange={(e) => updateEntry(iso, entry.id, "time", e.target.value)}
                            placeholder="9:00 am"
                            className="flex-1 min-w-0 px-2 py-1 text-sm border border-[hsl(var(--brand-border))] rounded"
                            data-testid={`time-${iso}-${entry.id}`}
                          />
                          {dateList.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => applySlotTimeToAllDays(iso, entry.id)}
                              className="p-1.5 rounded hover:bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))] hover:text-[hsl(var(--brand-sage))]"
                              data-testid={`apply-time-${iso}-${entry.id}`}
                              title={`Apply this time to every "${entry.label || "slot"}" across the week`}
                            >
                              <Clock className="w-3.5 h-3.5" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div>
                        <MealAutocomplete
                          value={entry.text}
                          onChange={(v) => updateEntry(iso, entry.id, "text", v)}
                          onPickRecipe={(r) => attachRecipe(iso, entry.id, r)}
                          excludeRecipeIds={entry.recipe_ids || []}
                          testid={`meal-${iso}-${entry.id}`}
                        />
                        {/* Recipe chips — sit directly under the meal input */}
                        {(entry.recipe_ids || []).length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`recipes-${iso}-${entry.id}`}>
                            {entry.recipe_ids.map((rid) => {
                              const r = recipeMap[rid];
                              return (
                                <span key={rid} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 bg-[hsl(var(--brand-sage))]/10 text-[hsl(var(--brand-sage))] border border-[hsl(var(--brand-sage))]/30 rounded-full text-xs">
                                  <Sparkles className="w-3 h-3" /> {r?.name || "Recipe"}
                                  <button onClick={() => detachRecipe(iso, entry.id, rid)} className="hover:bg-white/60 rounded-full p-0.5" data-testid={`detach-${rid}`}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                      <textarea
                        value={entry.remarks}
                        onChange={(e) => updateEntry(iso, entry.id, "remarks", e.target.value)}
                        placeholder="Remarks / preparation note"
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-[hsl(var(--brand-border))] rounded-md resize-y"
                        data-testid={`remarks-${iso}-${entry.id}`}
                      />
                      <div className="flex flex-col gap-1 min-w-[120px]">
                        <button
                          onClick={() => setPicker({ iso, entryId: entry.id })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--brand-cream-deep))] hover:bg-[hsl(var(--brand-cream))] text-xs border border-[hsl(var(--brand-border))]"
                          data-testid={`attach-recipe-${iso}-${entry.id}`}
                        >
                          <LinkIcon className="w-3 h-3" /> Attach recipe
                        </button>
                        <button
                          onClick={() => removeEntry(iso, entry.id)}
                          className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--destructive))] hover:underline justify-center mt-1"
                          data-testid={`remove-entry-${iso}-${entry.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete slot
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="p-3 bg-[hsl(var(--brand-cream))]/40">
                  <button
                    onClick={() => addEntry(iso)}
                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md hover:bg-[hsl(var(--brand-cream-deep))] text-sm text-[hsl(var(--brand-muted))] border border-dashed border-[hsl(var(--brand-border))]"
                    data-testid={`add-slot-${iso}`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add slot
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <RecipePicker
        open={!!picker}
        onClose={() => setPicker(null)}
        exclude={picker ? (plan.days[picker.iso]?.entries?.find((e) => e.id === picker.entryId)?.recipe_ids || []) : []}
        onPick={(r) => {
          if (picker) attachRecipe(picker.iso, picker.entryId, r);
          setPicker(null);
        }}
      />

      {tplModal ? (
        <TemplateModal
          modal={tplModal}
          dayTpls={dayTpls}
          weekTpls={weekTpls}
          planDayCount={dateList.length}
          onClose={() => setTplModal(null)}
          onSaveDay={(name) => saveDayAsTemplate(tplModal.iso, name)}
          onApplyDay={(tplId) => applyDayTemplate(tplModal.iso, tplId)}
          onSaveWeek={(name) => saveWeekAsTemplate(name)}
          onApplyWeek={(tplId) => applyWeekTemplate(tplId)}
          onDelete={(tplId) => deleteTemplate(tplId)}
        />
      ) : null}

      {dupModal ? (() => {
        const sourceLabel = formatDate(dupModal.sourceIso);
        const otherDays = dateList.filter((iso) => iso !== dupModal.sourceIso);
        const allSelected = otherDays.length > 0 && otherDays.every((iso) => dupModal.selected.has(iso));
        const toggle = (iso) => {
          const next = new Set(dupModal.selected);
          if (next.has(iso)) next.delete(iso); else next.add(iso);
          setDupModal({ ...dupModal, selected: next });
        };
        const toggleAll = () => {
          setDupModal({
            ...dupModal,
            selected: allSelected ? new Set() : new Set(otherDays),
          });
        };
        const confirm = () => {
          duplicateDayToOthers(dupModal.sourceIso, Array.from(dupModal.selected));
          setDupModal(null);
        };
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={() => setDupModal(null)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-6 border border-[hsl(var(--brand-border))] shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="duplicate-day-modal">
              <div className="font-display text-2xl">Duplicate {sourceLabel.weekday}'s plan</div>
              <p className="text-sm text-[hsl(var(--brand-muted))] mt-1">
                Copy every meal from {sourceLabel.weekday} ({sourceLabel.short}) to the days you pick below.
              </p>
              <div className="mt-4 px-3 py-2 rounded-md bg-[hsl(var(--brand-cream-deep))] text-xs text-[hsl(var(--brand-muted))]">
                ⚠ Existing meals on the selected days will be <strong>replaced</strong>.
              </div>
              {otherDays.length > 1 ? (
                <button onClick={toggleAll} className="mt-4 text-xs text-[hsl(var(--brand-sage))] hover:underline" data-testid="duplicate-day-select-all">
                  {allSelected ? "Clear all" : "Select all other days"}
                </button>
              ) : null}
              <div className="mt-3 max-h-64 overflow-y-auto space-y-1.5">
                {otherDays.map((iso) => {
                  const f = formatDate(iso);
                  const checked = dupModal.selected.has(iso);
                  return (
                    <label key={iso} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[hsl(var(--brand-cream))] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(iso)}
                        data-testid={`duplicate-day-pick-${iso}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{f.weekday}</div>
                        <div className="text-xs text-[hsl(var(--brand-muted))]">{f.short}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setDupModal(null)} className="px-4 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))] text-sm">Cancel</button>
                <button
                  onClick={confirm}
                  disabled={dupModal.selected.size === 0}
                  className="px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-50 text-sm"
                  data-testid="duplicate-day-confirm"
                >
                  Duplicate to {dupModal.selected.size || "…"} day{dupModal.selected.size === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </div>
        );
      })() : null}
    </div>
  );
}
